import { Component, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';

import { AnchorDirective, DeviceService, DynamicHtmlDirective } from '@frontend/vanilla/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgxPopperjsContentComponent, NgxPopperjsDirective, NgxPopperjsPlacements, NgxPopperjsTriggers } from 'ngx-popperjs';
import { Subscription } from 'rxjs';

import { Config, InputType, ProductType } from '../../../../config/appConstants';
import { DepositInfo } from '../../../../model/depositInfo.model';
import { MessageType } from '../../../../model/message.model';
import { QDInstrument } from '../../../../model/qdInstrument.model';
import { BehaviorDataService } from '../../../../service/behaviorData.service';
import { DataService } from '../../../../service/data.service';
import { DepositService } from '../../../../service/deposit.service';
import { OptionListService } from '../../../../service/optionList.service';
import { ResizeService } from '../../../../service/resize.service';
import { UserDataService } from '../../../../service/userDataService';
import { MessageComponent } from '../../../../shared/components/messages/message-component';

@Component({
    selector: 'app-product-preference',
    templateUrl: 'product-preference.component.html',
    imports: [
        DynamicHtmlDirective,
        MessageComponent,
        FormsModule,
        ReactiveFormsModule,
        NgxPopperjsDirective,
        NgxPopperjsContentComponent,
        TranslateModule,
        AnchorDirective,
    ],
})
export class ProductPreferenceComponent implements OnInit, OnChanges, OnDestroy {
    private userDataService = inject(UserDataService);
    private behaviorData = inject(BehaviorDataService);
    depositService = inject(DepositService);
    private optionListService = inject(OptionListService);
    private dataService = inject(DataService);
    private deviceService = inject(DeviceService);
    private resizeService = inject(ResizeService);

    @Input() productPreferenceFormGroup: FormGroup;
    subscriptions$: Subscription[] = [];
    type: MessageType;
    germanAMLChangesEnabled: boolean;
    checkBoxValue: boolean;
    showProductCheckBoxDE: boolean;
    graceEndedforDE: boolean;
    isGbgVerified: boolean;
    productSelectionError: boolean;
    isGamingLimitSet: boolean;
    isGamingLimitEnabled: boolean;
    productPreferencePokerOrCasino = false;
    isBrandSupportOnlyCasinoProduct: boolean;
    isBrandSupportOnlyPokerProduct: boolean;
    isBrandSupportBothCasinoAndPokerProduct: boolean;
    depositLimitsUrl: string;
    paypalOnlyForSports: boolean;
    visaOnlyForSports: boolean;
    hideProductRadioButtons: boolean;

    productList: any[];
    productOptionDisabled: boolean;
    selectedOption: QDInstrument;
    depositInfo: DepositInfo;
    isMobile: boolean = false;
    public get messageType(): typeof MessageType {
        return MessageType;
    }

    constructor() {
        this.isMobile = this.deviceService.isMobile;
    }

    ngOnInit(): void {
        this.onInitMethod();
    }

    private onInitMethod() {
        this.selectedOption = this.optionListService?.selectedOption;
        this.depositInfo = this.depositService?.depositInfo;
        const configParams = this.userDataService?.userData?.configParams;
        if (this.userDataService?.userData?.configParams) {
            this.germanAMLChangesEnabled = this.checkBoxValue = configParams?.germanAMLChangesEnabled === Config.YES;
            this.showProductCheckBoxDE = configParams?.showProductCheckBoxDE ? configParams?.showProductCheckBoxDE === Config.YES : false;
            this.isGamingLimitSet = this.germanAMLChangesEnabled && configParams?.isGamingLimitSet === Config.YES;
            this.isGamingLimitEnabled = configParams?.isGamingLimitEnabled === Config.YES;

            this.isBrandSupportOnlyCasinoProduct = configParams?.isBrandSupportOnlyCasinoProduct === Config.YES;
            this.isBrandSupportOnlyPokerProduct = configParams?.isBrandSupportOnlyPokerProduct === Config.YES;
            this.isBrandSupportBothCasinoAndPokerProduct = configParams?.isBrandSupportBothCasinoAndPokerProduct === Config.YES;

            this.depositLimitsUrl = configParams?.depositLimitsUrl;
            this.productList = this.userDataService?.userData?.productList;

            this.productOptionDisabled =
                (this.selectedOption?.paymentMethodInfo?.paymentMethod === InputType.PAYPAL && this.paypalOnlyForSports) ||
                (this.selectedOption?.paymentMethodInfo?.paymentMethod === InputType.VISA && this.visaOnlyForSports);
            this.paypalOnlyForSports = configParams?.paypalOnlyForSports === Config.YES;
            this.visaOnlyForSports = configParams?.visaOnlyForSports === Config.YES;
            this.hideProductRadioButtons = configParams?.hideProductRadioButtons === Config.YES || this.productOptionDisabled;
        }
        this.behaviorData.changeHideProductRadioButtons(this.hideProductRadioButtons);
        if (this.germanAMLChangesEnabled && !this.hideProductRadioButtons) {
            this.productOptionDisabled =
                (this.selectedOption?.paymentMethodInfo?.paymentMethod === InputType.PAYPAL && this.paypalOnlyForSports) ||
                (this.selectedOption?.paymentMethodInfo?.paymentMethod === InputType.VISA && this.visaOnlyForSports);
            let product =
                this.dataService?.sessionInfo?.productId === ProductType.POKER && this.isBrandSupportBothCasinoAndPokerProduct
                    ? ProductType.CASINO
                    : this.dataService?.sessionInfo?.productId;
            if (this.productOptionDisabled) {
                if (this.productList && (this.productList.includes(ProductType.SPORTS) || this.productList.includes(ProductType.SPORTSBOOK))) {
                    product = ProductType.SPORTSBOOK;
                }
                this.behaviorData.changeProductPreference(product);
            }
        }

        const product =
            this.dataService?.sessionInfo?.productId === ProductType.POKER && this.isBrandSupportBothCasinoAndPokerProduct
                ? ProductType.CASINO
                : this.dataService?.sessionInfo?.productId;
        this.behaviorData.changeProductPreference(
            this.productOptionDisabled && this.productList && this.productList.includes(ProductType.SPORTS) ? ProductType.SPORTSBOOK : product,
        );

        if (this.germanAMLChangesEnabled && configParams?.isSetSportsByDefault === Config.YES) {
            this.behaviorData.changeProductPreference(ProductType.SPORTSBOOK);
        }

        if (this.germanAMLChangesEnabled) {
            this.isGbgVerified = configParams?.gbgVerified === Config.YES;
            this.graceEndedforDE = configParams?.gracePeriodEnded === Config.YES;
        }
        if (
            this.depositService?.depositInfo?.productPreference.toUpperCase() === ProductType.POKER ||
            this.depositService?.depositInfo?.productPreference.toUpperCase() === ProductType.CASINO
        )
            this.productPreferencePokerOrCasino = true;

        this.createForm();
        this.getBehaviorData();
    }

    ngOnChanges() {
        this.onInitMethod();
    }

    getBehaviorData() {
        this.subscriptions$.push(
            this.behaviorData.getProductSelectionError.subscribe((data) => {
                this.productSelectionError = data;
            }),
        );
    }

    createForm() {
        this.productPreferenceFormGroup.addControl('productPreference', new FormControl(this.productList[0]));
        this.updateProductPreference(this.productList[0]);
    }

    updateProductPreference(product: any) {
        this.depositInfo.productPreference = product;
        this.checkBoxValue = true;
        this.behaviorData.changeProductPreference(product);
        this.behaviorData.changeSportsLimitsErrorDetails(null);
        this.behaviorData.changeGamingLimitsErrorDetails(null);
        this.resizeService.resizeCashier();
    }

    ngOnDestroy() {
        this.subscriptions$.map((subscription) => subscription.unsubscribe());
    }

    protected readonly NgxPopperjsTriggers = NgxPopperjsTriggers;
    protected readonly NgxPopperjsPlacements = NgxPopperjsPlacements;
}
